import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { DataOrigin, DatasetSchemaVersion } from "./dataset";
import { supportsStorageFeature, type DatasetStorageBackend } from "./datasetStorageBackend";
import type { Embodiment, Tip } from "./embodiment";
import type { PageQuery, PageResp } from "./page";
import { getUploadSessionToken } from "./uploadSessionToken";

// 데이터셋 업로드 잡(files 모드) API 클라이언트다. backend /v1/job/dataset/upload* 와 대응한다.
//
// Owns:
// - UploadJob 계약 타입(FE에서 쓰는 필드만)과 모든 lifecycle 호출(init·declare·probe·
//   staged 삭제·complete·cancel·잡 삭제·reset·reprocess·heartbeat·labels).
// - X-Upload-Session-Token 을 붙이는 범위: 쓰기(lease 참여)와 소유 판정 읽기(owned_by_me)에만 붙인다.
//
// Does not own:
// - 파일 바이트 전송(GCS signed PUT). src/upload/putFile.ts 가 담당한다(registry 는 control-plane).
// - 업로드 실행 순서와 재시도 정책. src/upload/UploadEngine.ts 가 담당한다.

export type DatasetUploadStatus =
  | "pending_upload"
  | "uploaded"
  // files/archive 공통 worker 수집 단계다. worker 가 staging tree 를 다운로드한다.
  | "fetching"
  | "probing"
  | "validating"
  | "migrating"
  // manifest 확정 이후의 반입 단계다. 서버사이드 copy → 검증 → 청크 commit 이 cursor 를
  // 앞으로 옮긴다. finalize 가 카탈로그를 닫고 Dataset 을 available 로 바꾼다.
  | "publishing"
  | "done"
  | "failed"
  | "duplicate_skipped"
  | "conflict"
  | "cancelled";

// 목록의 coarse 상태 그룹이다. 서버가 그룹을 멤버 status 집합으로 펼친다. 그래서 FE 는 매핑을
// 가지지 않는다.
export type UploadStatusGroup = "all" | "uploading" | "done" | "failed" | "cancelled";

/** 업로드 목록 조회 범위. `all`은 관리자 전용이다. */
export type DatasetUploadListScope = "me" | "all";

// 예약 레이블 선택 맵이다. key → 선택된 값 목록.
export type UploadReservedLabels = Record<string, string[]>;

// 이 화면이 만드는 업로드의 정본 위치다. 화면은 GCS 만 지정한다. Azure 는 MCP·API 업로드가 지정한다.
// 그래서 응답의 `target_backend` 는 이 타입이 아니라 `DatasetStorageBackend` 다.
export type DatasetUploadStorageBackend = "gcp_storage";

// status=failed 일 때 오는 실패 단계·코드다. 백엔드가 내부 error 에서 노출해도 안전한 값만 파생한다.
export type DatasetUploadFailure = {
  stage: string;
  code: string;
};

// 반입 재개점 스냅샷이다. backend service/dataset_upload/contract/schema.py::ImportProgressResp
// 와 1:1 이다. cursor 는 에피소드 절대 index 다. `[0, cursor)` 가 끝난 구간이다.
//   - copy: 정본 바이트를 core 로 옮긴 구간
//   - commit: 카탈로그까지 확정한 구간(copy >= commit)
// `generation` 은 점유 세대이고, `attempts` 는 점유 횟수다. 다른 워커가 잡을 다시 점유하면 두 값이
// 오른다. 워커가 종료하며 잡을 반납하면 `attempts` 는 1 줄어든다. 그래서 `attempts` 는 단조 증가하지
// 않는다.
export type DatasetUploadImportProgress = {
  generation: number;
  attempts: number;
  episode_count: number;
  file_count: number;
  copy_cursor: number;
  commit_cursor: number;
};

export type DatasetUploadJob = {
  uuid: string;
  organization_uuid: string;
  created_by_member_uuid: string | null;
  artifact_source_uuid: string | null;
  dataset_uuid: string | null;
  // 업로더가 선택한 하드웨어 프로필이다. 선택하지 않았으면 null 이다(리뷰에서 확정한다).
  // 재업로드 pre-fill 의 소스다.
  embodiment_uuid: string | null;
  // embodiment_uuid 의 nested 객체다. 목록 식별 컬럼과 재업로드 pre-fill 에 추가 조회 없이 쓴다.
  embodiment: Embodiment | null;
  // 선택한 EE 말단 tip uuid 다(재업로드 pre-fill 용). 선택하지 않았으면 null 이다.
  tip_uuid: string | null;
  // tip_uuid 의 nested 객체다. embodiment 와 달리 라이프사이클 응답에도 온다(평탄한 row 라서
  // 서버가 추가 조회 없이 포함한다). 재업로드 폼은 이 값으로 tip 을 **동기로 시드**한다.
  // 카탈로그를 다시 받아 채우면, 그 사이에 폼의 빈 tip 이 "비움"으로 제출되어 서버 값을 지운다.
  tip: Tip | null;
  display_name: string;
  // 이 잡의 정본 위치다. MCP·API 로 만든 Azure 업로드도 업로드 현황 목록에 나온다.
  target_backend: DatasetStorageBackend;
  target_object_key_prefix: string | null;
  // 현재 raw upload가 쓰는 staging prefix다. reset 응답에서는 새 nonce prefix다.
  staging_object_key: string;
  status: DatasetUploadStatus;
  // publishing 단계의 서버 측 진행률이다(0~100). 다른 상태에서는 null 이다. 서버가
  // `(copy_cursor + commit_cursor) / 2N` 으로 계산한다. 그래서 copy 단계에서도 값이 바뀐다.
  publish_progress_percent: number | null;
  // 반입 재개점이다. **manifest 확정 전이면 null 이다. null 은 "0 에피소드"가 아니다.**
  // 규모를 아직 모른다는 뜻이다. 그래서 건수를 0 으로 바꾸면 안 된다.
  import_progress: DatasetUploadImportProgress | null;
  detected_schema_version: DatasetSchemaVersion | null;
  migration_description: string | null;
  // status=failed 일 때의 실패 단계·코드다.
  failure: DatasetUploadFailure | null;
  // complete 가 실측한 총 바이트다. complete 전과 승격 잡(MD 공용 승격)에서는 0 이다.
  declared_size_bytes: number;
  // complete 할 때 서버가 실측한 staged 파일 수다. 승격 잡과 complete 전에는 null 이다.
  staged_file_count: number | null;
  // 채널 파일이 없어서 invalid 인 에피소드 수다. 업로드가 이 에피소드를 제외했다. 잡이 DONE 이 될 때
  // 한 번 센다. **null 은 "0 개" 가 아니다.** DONE 전이거나 이 필드보다 먼저 끝난 잡이라서 모른다.
  missing_channel_episode_count: number | null;
  upload_verified: boolean;
  // 업로드 중 서버에 저장한 예약 레이블이다. 재업로드 pre-fill 의 소스다. 저장하지 않았으면 null 이다.
  reserved_labels: UploadReservedLabels | null;
  // seed Dataset 에 선언된 행동 주체다. 재업로드 pre-fill 의 소스다. null 이면 선언하지 않은
  // 것이다. 그러면 complete 가 422 dataset.data_origin_missing 으로 막는다.
  // 이 값은 잡이 아니라 seed Dataset 이 소유한다.
  data_origin: DataOrigin | null;
  // seed Dataset 의 컬렉션 밴드·큐레이션 집계 선언이다. 에피소드 결함 검사는 이 값과 관계없이
  // 항상 실행된다. 재업로드 pre-fill 의 소스다. null 은 seed 가 정리된 잡(cancel 이후)이라서
  // 읽을 값이 없다는 뜻이다. 그때 폼은 기본값(산출)을 쓴다.
  band_diagnostics_enabled: boolean | null;
  // 데이터셋 단위 lease 상태다. 만료 판정은 client 시각이 아니라 서버 값을 신뢰한다.
  lease_active: boolean;
  lease_expires_at: string | null;
  // 내 세션 토큰이 이 lease 를 소유하는지 나타낸다(서버가 계산). 목록/단건 GET 에
  // X-Upload-Session-Token 을 담으면 서버가 요청 토큰과 owner 를 비교해 채운다.
  // 토큰을 붙이지 않으면 false 다.
  owned_by_me: boolean;
  created_at: string;
  modified_at: string;
};

// init 요청이다. 서버가 금지하는 declared_size_bytes(422 upload.size_forbidden)는
// 타입에서 제외한다.
export type DatasetUploadInitReq = {
  display_name: string;
  embodiment_uuid?: string | null;
  tip_uuid?: string | null;
  // 업로더가 선언하는 행동 주체다. 서버는 이 값을 seed Dataset 에 바로 쓴다. 생략해도 init 은
  // 통과하지만 complete 가 거부한다. 그래서 폼이 먼저 필수 입력으로 막는다.
  data_origin?: DataOrigin | null;
  // 컬렉션 밴드·큐레이션 집계를 산출할지 정한다(에피소드 결함 검사는 항상 실행한다). 생략하면
  // 서버가 산출로 읽는다. 서버는 이 값을 라벨에서 유도하지 않는다. 그래서 산출하지 않으려면
  // 명시해야 한다.
  band_diagnostics_enabled?: boolean;
  // 이번 업로드가 파일을 올릴 서버 위치다(listDatasetUploadLocations 의 code). **키가 없는 것과
  // 명시 null 은 같은 뜻이다(조직 기본 위치).** 서버가 `is not None` 으로만 구분하므로 두 경우를
  // 합쳐도 안전하다. 이 값은 잡 생성 때 고정되고 이후 바꿀 수 없다. 그래서 reset 은 이 축을 받지
  // 않는다(422 extra).
  upload_location_code?: string | null;
  target_backend: DatasetUploadStorageBackend;
};

// 파일을 올릴 수 있는 업로드 서버 위치 1개다. `GET /v1/job/dataset/upload-locations` 의 항목이다.
// 버킷명·리전·내부 id 는 응답에 없다. 위치를 지정하는 값은 code 뿐이다. 그래서 버킷을 바꿔도
// 클라이언트 계약은 바뀌지 않는다. 정본(core) 버킷은 목록에 없다.
export type DatasetUploadLocation = {
  code: string;
  display_name: string;
  // 이 위치를 **언제 고르는지** 설명한다. 이름만으로는 회선 상황에 어느 위치가 나은지 알 수 없다.
  // 그래서 서버가 이 설명을 따로 보낸다. 화면은 문장을 만들지 않고 그대로 표시한다.
  // 그래서 사람과 MCP 에이전트가 같은 문장을 본다.
  description: string;
  // 위치를 지정하지 않은 업로드가 가는 위치다. 목록에서 정확히 하나가 true 다.
  is_default: boolean;
};

// declare 대상 파일 1개다. size_bytes 는 optional 참고값이다. 서버 계약이 gt=0 이라서
// 0바이트 파일은 필드를 생략해야 한다(넣으면 422). 이 정규화는 호출부가 아니라
// declareDatasetUploadFiles 가 소유한다. 그래서 호출부는 아는 크기를 그대로 넣어도 된다.
export type UploadFileDeclareItem = {
  relative_path: string;
  size_bytes?: number;
};

// declare 응답의 파일 1개다. 정규화된 경로와 one-shot signed PUT URL 을 담는다. 만료는 티켓별
// upload_url_expires_at 만 신뢰한다. TTL 상수를 하드코딩하지 않는다. TTL 은 env 로 바뀔 수 있다.
export type UploadFileTicket = {
  relative_path: string;
  upload_url: string;
  upload_method: "PUT";
  upload_url_expires_at: string;
  // PUT 에 그대로 담아야 하는 헤더다. 값은 이 잡의 staging 위치가 정한다. GCS 면 비어 있다. Azure 면
  // `x-ms-blob-type: BlockBlob` 이 들어 있고, 빠뜨리면 storage 가 400 으로 거절한다. 이 화면은 Azure
  // 업로드를 새로 만들지 않는다. 그러나 MCP·API 로 만든 Azure 잡을 업로드 현황에서 재업로드할 수 있다.
  upload_headers: Record<string, string>;
};

// staged 파일 목록 행이다. 서버가 staging prefix 를 실측(probe)한 결과다. declare 만 하고
// PUT 하지 않은 경로는 목록에 없다. backend 메타가 없으면 size_bytes 는 null 이다.
export type StagedFile = {
  relative_path: string;
  size_bytes: number | null;
};

// declare 배치당 최대 경로 수다(backend DECLARE_BATCH_MAX_FILES 와 같은 값). 파일이 더 많으면
// 여러 번 declare 한다(횟수 제한 없음).
export const DECLARE_BATCH_MAX_FILES = 1_000;

export type DatasetUploadResetReq = {
  reserved_labels?: UploadReservedLabels | null;
  // 카탈로그 두 축은 3-상태다. **키 부재=유지, 명시 null=비움, uuid=변경.** 다른 필드에서는
  // null 이 유지를 뜻한다. 그래서 두 축을 다른 필드와 같은 규칙으로 읽지 않는다.
  // 조립은 buildDatasetUploadResetReq 가 소유한다.
  embodiment_uuid?: string | null;
  tip_uuid?: string | null;
  // 행동 주체를 다시 선언한다(optional). null 이면 seed Dataset 의 기존 값을 유지한다.
  data_origin?: DataOrigin | null;
  // 밴드 집계 산출을 다시 선언한다(optional). 생략하거나 null 이면 seed Dataset 의 기존 값을 유지한다.
  band_diagnostics_enabled?: boolean | null;
};

// reset 폼 값이다. 재업로드 다이얼로그가 가진 선택이다. 두 카탈로그 축의 null 은 **사용자가
// 비운 상태**다. 폼은 baseline 의 nested row 로 동기 시드한다. 그래서 "아직 읽지 못함"이라는
// 중간 상태가 없다. 비동기로 채우면 그 사이의 null 이 비움으로 제출되어 서버 값을 지운다.
export type DatasetUploadResetSelection = {
  reservedLabels: UploadReservedLabels;
  embodimentUuid: string | null;
  tipUuid: string | null;
  dataOrigin: DataOrigin | null;
  bandDiagnosticsEnabled: boolean;
};

/**
 * reset payload 를 조립한다. 카탈로그 두 축의 3-상태(유지/비움/변경)를 조립하는 유일한 지점이다.
 *
 * baseline(reset 대상 잡)과 **다를 때만** 키를 넣는다. 같으면 키를 생략해 "유지"로 둔다.
 * 다르면 폼 값을 그대로 넣는다. 그러면 uuid 는 변경, null 은 비움이 된다. 같은 값을 다시 넣어도
 * 서버 결과는 같다. 하지만 생략하면 "바꾸지 않음"이라는 의도가 wire 에 남는다.
 *
 * 두 축을 서로 연결하지 않는다. embodiment 를 비울 때 tip 도 비우는 것은 폼 규칙이다. 그 결과는
 * 이미 selection 에 반영되어 두 키로 나간다. 여기서 다시 연결하면 같은 규칙이 두 곳에 생긴다.
 */
export function buildDatasetUploadResetReq(
  baseline: DatasetUploadJob,
  selection: DatasetUploadResetSelection,
): DatasetUploadResetReq {
  const req: DatasetUploadResetReq = {
    reserved_labels: selection.reservedLabels,
    data_origin: selection.dataOrigin,
    band_diagnostics_enabled: selection.bandDiagnosticsEnabled,
  };
  if (selection.embodimentUuid !== baseline.embodiment_uuid) {
    req.embodiment_uuid = selection.embodimentUuid;
  }
  if (selection.tipUuid !== baseline.tip_uuid) {
    req.tip_uuid = selection.tipUuid;
  }
  return req;
}

// staged 파일 목록의 총 용량과, 그중 크기를 모르는 건수를 계산한다. `size_bytes` 의 null 은 0 이
// 아니라 "backend 메타 없음"이다. `?? 0` 으로 합산하면 경고 없이 합계가 작아진다. 전부 null 이면
// "0.0 B" 가 되어 빈 업로드로 보인다. 그래서 합계와 미상 건수를 함께 반환한다. 표시하는 쪽은
// 두 값을 모두 표시한다.
export function stagedFilesTotalBytes(files: readonly StagedFile[]): {
  bytes: number;
  unknownCount: number;
} {
  let bytes = 0;
  let unknownCount = 0;
  for (const file of files) {
    if (file.size_bytes === null) {
      unknownCount += 1;
      continue;
    }
    bytes += file.size_bytes;
  }
  return { bytes, unknownCount };
}

// 재업로드 pre-fill 의 예약 레이블이다. 저장하지 않은 값(null)은 "고른 것이 없음"이다. 그래서
// 빈 맵으로 연다. 잡의 값을 공유하지 않고 새 맵을 만든다. 그래서 폼 편집이 응답 캐시를 바꾸지 않는다.
export function prefillReservedLabels(
  job: Pick<DatasetUploadJob, "reserved_labels">,
): UploadReservedLabels {
  return { ...(job.reserved_labels ?? {}) };
}

/**
 * 이 잡의 정본 위치가 밴드 진단을 지원하는지 판정한다.
 *
 * 재업로드 폼과 선언의 초기값은 같은 기능 지원표를 따른다.
 */
export function uploadJobSupportsBandDiagnostics(
  job: Pick<DatasetUploadJob, "target_backend">,
): boolean {
  return supportsStorageFeature(job.target_backend, "diagnostics");
}

// 재업로드 pre-fill 의 밴드 집계 산출 선언이다. null 은 seed 가 정리된 잡(cancel 이후)이라서
// 읽을 값이 없다는 뜻이다. 그때는 서버 기본값과 같은 "산출"로 연다. 이 폴백은 이 경계가 소유한다.
// 정본 위치가 진단을 지원하지 않는 잡은 항상 끈다.
export function prefillBandDiagnosticsEnabled(
  job: Pick<DatasetUploadJob, "band_diagnostics_enabled" | "target_backend">,
): boolean {
  return uploadJobSupportsBandDiagnostics(job) && (job.band_diagnostics_enabled ?? true);
}

// 실측한 총 바이트를 반환한다. 없으면 null 이다. declared_size_bytes 의 0 은 크기가 아니라
// "실측 없음" sentinel 이다(complete 전 잡과 승격 잡). 0 을 그대로 포맷하면 "0.0 B" 가 되어 빈
// 업로드와 구분되지 않는다. 그래서 표시하는 쪽은 이 헬퍼로 미실측을 먼저 구분한다.
export function measuredUploadSizeBytes(
  job: Pick<DatasetUploadJob, "declared_size_bytes">,
): number | null {
  return job.declared_size_bytes > 0 ? job.declared_size_bytes : null;
}

// 화면이 표시할 업로드 위치를 반환한다. 사용자가 고른 위치가 있으면 그 위치다. 아직 고르지
// 않았으면(null) 기본 위치다.
//
// **표시 전용 폴백이다.** 이 결과 code 를 init payload 로 보내면 "조직 기본값 위임"이
// "이 code 고정"으로 바뀐다(rules §16). 폼은 사용자가 실제로 고르기 전까지 null 을 가진다.
// 화면만 이 헬퍼로 실제로 업로드할 위치를 표시한다.
//
// 목록에 없는 code 는 null 을 반환한다. 표시할 값을 만들지 않는다(카탈로그가 바뀌었다는 신호다).
export function displayedUploadLocation(
  locations: readonly DatasetUploadLocation[],
  selectedCode: string | null,
): DatasetUploadLocation | null {
  if (selectedCode !== null) {
    return locations.find((location) => location.code === selectedCode) ?? null;
  }
  return locations.find((location) => location.is_default) ?? null;
}

export type DatasetUploadListQuery = PageQuery & {
  // 내 업로드(me) 또는 전체 업로드(all)다. all이면 서버도 관리자 권한을 검증한다.
  scope: DatasetUploadListScope;
  // 다중 status(IN)다. 같은 status 그룹의 여러 status 를 반복 키로 보낸다(?status=a&status=b).
  status?: readonly DatasetUploadStatus[];
  // coarse 상태 그룹이다. 서버가 그룹을 멤버 status 로 펼치고, status 와 OR 로 합친다.
  status_group?: UploadStatusGroup;
};

// 세션 토큰 헤더를 붙인다. 쓰기 호출(lease 참여)과 소유 판정이 필요한 읽기(목록/단건의
// owned_by_me 계산)에만 붙인다. 그래서 이 모듈 밖 엔드포인트로 토큰이 노출되지 않는다.
function sessionTokenConfig(): { headers: Record<string, string> } {
  return { headers: { "X-Upload-Session-Token": getUploadSessionToken() } };
}

// 고를 수 있는 업로드 서버 위치 목록을 조회한다. 세션 토큰을 붙이지 않는다. 이 목록은 조직
// 공용 카탈로그다. lease 에 참여하지 않고, 소유(owned_by_me) 판정도 없다.
export async function listDatasetUploadLocations(): Promise<DatasetUploadLocation[]> {
  const response = await apiClient.get<DatasetUploadLocation[]>(
    apiRoutes.datasetUpload.uploadLocations,
  );
  return response.data;
}

export async function initDatasetUpload(payload: DatasetUploadInitReq): Promise<DatasetUploadJob> {
  const response = await apiClient.post<DatasetUploadJob>(
    apiRoutes.datasetUpload.collection,
    payload,
    sessionTokenConfig(),
  );
  return response.data;
}

export async function listDatasetUploads(
  query: DatasetUploadListQuery,
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<DatasetUploadJob>> {
  const response = await apiClient.get<PageResp<DatasetUploadJob>>(
    apiRoutes.datasetUpload.collection,
    {
      params: query,
      signal: options.signal,
      // 배열 status 를 FastAPI 반복 키(?status=a&status=b)로 직렬화한다. axios 기본값인
      // `status[]=` 대괄호 형식은 backend 가 읽지 못한다. indexes:null 은 배열에만 적용된다.
      // page/size 스칼라에는 영향이 없다.
      paramsSerializer: { indexes: null },
      // owned_by_me 계산을 위해 세션 토큰을 담는다(재개 후보 탐지가 소유 lease 를 판정한다).
      ...sessionTokenConfig(),
    },
  );
  return response.data;
}

export async function getDatasetUpload(uploadUuid: string): Promise<DatasetUploadJob> {
  const response = await apiClient.get<DatasetUploadJob>(
    apiRoutes.datasetUpload.detail(uploadUuid),
    // owned_by_me 를 목록과 같은 방식으로 채우려고 단건 GET 에도 세션 토큰을 담는다.
    sessionTokenConfig(),
  );
  return response.data;
}

// size_bytes 를 뺄지 이 함수가 결정한다. 서버 계약이 gt=0 이라서 0바이트 파일과 크기를 모르는
// 파일은 필드를 빼고 보내야 한다(넣으면 422). 호출부마다 이 처리를 반복하면, 처리를 빠뜨린 새
// 호출자가 422 를 받는다. 그래서 wire 로 보내기 직전에 여기서 한 번 정규화한다.
function toDeclarePayloadItem(file: UploadFileDeclareItem): UploadFileDeclareItem {
  const { relative_path, size_bytes } = file;
  return size_bytes !== undefined && Number.isFinite(size_bytes) && size_bytes > 0
    ? { relative_path, size_bytes }
    : { relative_path };
}

// 파일을 선언하고 파일별 signed PUT 티켓을 받는다. 배치 ≤ DECLARE_BATCH_MAX_FILES 는
// 호출부(ticketStore)가 보장한다. declare 는 lease 를 새로 얻거나 갱신한다(첫 declare 가 lease 를 얻는다).
export async function declareDatasetUploadFiles(
  uploadUuid: string,
  files: readonly UploadFileDeclareItem[],
): Promise<UploadFileTicket[]> {
  const response = await apiClient.post<{ files: UploadFileTicket[] }>(
    apiRoutes.datasetUpload.files(uploadUuid),
    { files: files.map(toDeclarePayloadItem) },
    sessionTokenConfig(),
  );
  return response.data.files;
}

// staged 파일 목록(probe)을 조회한다. 서버가 GCS staging prefix 를 실측한다. lease 가 필요 없는
// 읽기라서 세션 토큰을 담지 않는다.
export async function listDatasetUploadStagedFiles(
  uploadUuid: string,
  query: PageQuery = {},
): Promise<PageResp<StagedFile>> {
  const response = await apiClient.get<PageResp<StagedFile>>(
    apiRoutes.datasetUpload.files(uploadUuid),
    { params: query },
  );
  return response.data;
}

// staged 파일을 삭제한다(멱등 204). complete 전 검토 단계에서 잘못 올린 파일을 지울 때 쓴다.
export async function deleteDatasetUploadStagedFile(
  uploadUuid: string,
  relativePath: string,
): Promise<void> {
  await apiClient.delete(apiRoutes.datasetUpload.files(uploadUuid), {
    params: { relative_path: relativePath },
    ...sessionTokenConfig(),
  });
}

// complete 본문이다. 서버가 staging 실측과 대조할 기대값을 싣는다. 두 필드는 모두 선택이다. 서버는
// 값이 있는 필드만 대조한다. 본문을 읽지 않는 서버는 값을 무시하고 실측만으로 완료한다. 본문을 읽는
// 서버(ZERG-1043)는 이 두 필드가 아닌 필드를 422 로 거부한다.
export type DatasetUploadCompleteReq = {
  expected_file_count?: number;
  expected_size_bytes?: number;
};

// 업로드를 완료한다. 서버가 staging prefix 를 실측해 총량과 개수를 검증하고 기록한다. 이미 UPLOADED 면
// 멱등 200 이다.
//
// 실측이 기대값과 다르면 서버는 409 upload.complete.staging_mismatch 로 거부한다. 그때 잡은
// pending_upload 그대로다. 선택 폴더를 모르는 호출부(검토 다이얼로그)는 기대값 없이 빈 body 를 보낸다.
export async function completeDatasetUpload(
  uploadUuid: string,
  payload: DatasetUploadCompleteReq = {},
): Promise<DatasetUploadJob> {
  const response = await apiClient.post<DatasetUploadJob>(
    apiRoutes.datasetUpload.complete(uploadUuid),
    payload,
    sessionTokenConfig(),
  );
  return response.data;
}

export async function cancelDatasetUpload(uploadUuid: string): Promise<DatasetUploadJob> {
  const response = await apiClient.post<DatasetUploadJob>(
    apiRoutes.datasetUpload.cancel(uploadUuid),
  );
  return response.data;
}

// 잡 행을 삭제한다(204, 생성자 또는 admin). 그 외 요청자는 403 `upload.delete.forbidden` 을 받는다.
// 되돌릴 수 없다. 서버는 seed 카탈로그도 함께 제거하고 staging 바이트를 비운다.
// 대상은 카탈로그를 확정하지 않고 끝난 잡뿐이다. 그 외 잡은 409 다.
export async function deleteDatasetUpload(uploadUuid: string): Promise<void> {
  await apiClient.delete(apiRoutes.datasetUpload.detail(uploadUuid));
}

// 재업로드: 잡을 리셋한다. 서버가 새 nonce staging prefix 를 발급하므로 기존 staged 바이트는
// 버려진다(모든 파일을 다시 전송한다). display_name 은 유지된다.
export async function resetDatasetUpload(
  uploadUuid: string,
  payload: DatasetUploadResetReq,
): Promise<DatasetUploadJob> {
  const response = await apiClient.post<DatasetUploadJob>(
    apiRoutes.datasetUpload.reset(uploadUuid),
    payload,
    sessionTokenConfig(),
  );
  return response.data;
}

// 재업로드 없는 재처리: FAILED + upload_verified 잡을 uploaded 로 되돌리고 워커가 다시 처리하게 한다.
export async function reprocessDatasetUpload(uploadUuid: string): Promise<DatasetUploadJob> {
  const response = await apiClient.post<DatasetUploadJob>(
    apiRoutes.datasetUpload.reprocess(uploadUuid),
    undefined,
    sessionTokenConfig(),
  );
  return response.data;
}

// lease 하트비트다(30s 주기). lease 를 잃었으면 서버가 409 upload.lease.held 로 응답한다.
export async function heartbeatDatasetUpload(uploadUuid: string): Promise<DatasetUploadJob> {
  const response = await apiClient.post<DatasetUploadJob>(
    apiRoutes.datasetUpload.heartbeat(uploadUuid),
    undefined,
    sessionTokenConfig(),
  );
  return response.data;
}

// 업로드 중에 예약 레이블을 서버에 저장한다. 완료할 때 서버 워커가 레이블을 적용한다.
export async function setDatasetUploadReservedLabels(
  uploadUuid: string,
  reservedLabels: UploadReservedLabels,
): Promise<UploadReservedLabels> {
  const response = await apiClient.put<{ reserved_labels: UploadReservedLabels }>(
    apiRoutes.datasetUpload.labels(uploadUuid),
    { reserved_labels: reservedLabels },
    sessionTokenConfig(),
  );
  return response.data.reserved_labels;
}
